/**
 * Copyright (c) 2025 eBUCIS, LLC. All rights reserved.
 *
 * Child Progress Card Component
 * Displays individual child's progress and statistics
 *
 * This software is proprietary and confidential. Unauthorized copying,
 * distribution, or modification is strictly prohibited.
 */

import { useNavigate } from 'react-router-dom'
import {
  Target,
  Clock,
  PlayCircle,
  BookOpen,
  Calendar,
  CheckCircle,
  FileText,
  TrendingUp,
  UserCheck,
  MessageSquare,
  FileSignature
} from 'lucide-react'
// Guardian-facing variant: "Awaiting your approval" (the guardian is the approver).
import { GUARDIAN_RELATIONSHIP_STATUS_BADGE as STATUS_BADGE } from '../utils/relationshipBadges'

const ChildProgressCard = ({ child, formatTime, onSignConsent }) => {
  const navigate = useNavigate()

  // Guardian consent is only applicable for minors (under 18).
  const isMinor = child.age != null && child.age < 18

  // Get initials for avatar
  const getInitials = () => {
    const first = child.firstName?.charAt(0) || ''
    const last = child.lastName?.charAt(0) || ''
    return (first + last).toUpperCase() || '?'
  }

  // Generate color based on child ID for consistent avatar colors
  const getAvatarColor = () => {
    const colors = [
      'bg-blue-500',
      'bg-green-500',
      'bg-purple-500',
      'bg-pink-500',
      'bg-indigo-500',
      'bg-yellow-500',
      'bg-red-500'
    ]
    // Guard against a nullish/non-string id so an unexpected API shape can't crash the card.
    // Use ?? (not ||) so a legitimate falsy id like 0 still hashes to itself.
    const hash = String(child.id ?? '')
      .split('')
      .reduce((acc, char) => acc + char.charCodeAt(0), 0)
    return colors[hash % colors.length]
  }

  const hasStats = child.stats && child.stats.totalChallenges > 0

  const handleViewDetails = () => {
    navigate(`/guardian/child/${child.id}`)
  }

  return (
    <div className="card hover:shadow-lg transition-all duration-200">
      <div className="card-body">
        <div className="flex flex-col sm:flex-row items-start gap-4">
          {/* Avatar and Basic Info */}
          <div className="flex items-start gap-4 flex-1 min-w-0">
            <div
              className={`w-14 h-14 rounded-full ${getAvatarColor()} flex items-center justify-center text-white font-bold text-xl flex-shrink-0`}
            >
              {getInitials()}
            </div>

            <div className="flex-1 min-w-0">
              <div className="flex items-start justify-between gap-2 mb-2">
                <div className="flex-1 min-w-0">
                  <h4 className="text-lg font-semibold text-gray-900 dark:text-gray-100 truncate">
                    {child.firstName} {child.lastName}
                  </h4>
                  <p className="text-sm text-gray-600 dark:text-gray-400 truncate">{child.email}</p>
                </div>
                {child.age && (
                  <span className="text-xs px-2.5 py-1 rounded-full bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 whitespace-nowrap font-medium">
                    {child.age} years
                  </span>
                )}
              </div>

              {/* Stats Grid - Always Visible */}
              {hasStats ? (
                <div className="space-y-4">
                  {/* Primary Stats */}
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    <div className="bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-900/20 dark:to-blue-800/20 rounded-lg p-3 border border-blue-200 dark:border-blue-800">
                      <div className="flex items-center gap-1.5 mb-1">
                        <BookOpen className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                        <span className="text-xs font-medium text-blue-700 dark:text-blue-300">
                          Total
                        </span>
                      </div>
                      <p className="text-xl font-bold text-blue-900 dark:text-blue-100">
                        {child.stats.totalChallenges}
                      </p>
                    </div>

                    <div className="bg-gradient-to-br from-green-50 to-green-100 dark:from-green-900/20 dark:to-green-800/20 rounded-lg p-3 border border-green-200 dark:border-green-800">
                      <div className="flex items-center gap-1.5 mb-1">
                        <CheckCircle className="w-4 h-4 text-green-600 dark:text-green-400" />
                        <span className="text-xs font-medium text-green-700 dark:text-green-300">
                          Completed
                        </span>
                      </div>
                      <p className="text-xl font-bold text-green-900 dark:text-green-100">
                        {child.stats.completedChallenges || 0}
                      </p>
                    </div>

                    <div className="bg-gradient-to-br from-purple-50 to-purple-100 dark:from-purple-900/20 dark:to-purple-800/20 rounded-lg p-3 border border-purple-200 dark:border-purple-800">
                      <div className="flex items-center gap-1.5 mb-1">
                        <PlayCircle className="w-4 h-4 text-purple-600 dark:text-purple-400" />
                        <span className="text-xs font-medium text-purple-700 dark:text-purple-300">
                          In Progress
                        </span>
                      </div>
                      <p className="text-xl font-bold text-purple-900 dark:text-purple-100">
                        {child.stats.inProgressChallenges || 0}
                      </p>
                    </div>

                    <div className="bg-gradient-to-br from-amber-50 to-amber-100 dark:from-amber-900/20 dark:to-amber-800/20 rounded-lg p-3 border border-amber-200 dark:border-amber-800">
                      <div className="flex items-center gap-1.5 mb-1">
                        <Clock className="w-4 h-4 text-amber-600 dark:text-amber-400" />
                        <span className="text-xs font-medium text-amber-700 dark:text-amber-300">
                          Time
                        </span>
                      </div>
                      <p className="text-xl font-bold text-amber-900 dark:text-amber-100">
                        {formatTime(child.stats.totalTimeSpent)}
                      </p>
                    </div>
                  </div>

                  {/* Secondary Stats */}
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    {/* Average Score */}
                    {child.stats.averageScore !== null && (
                      <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-2.5">
                        <div className="flex items-center gap-1.5 mb-1">
                          <Target className="w-3.5 h-3.5 text-gray-500 dark:text-gray-400" />
                          <span className="text-xs text-gray-600 dark:text-gray-400">
                            Avg Score
                          </span>
                        </div>
                        <p className="text-base font-semibold text-gray-900 dark:text-gray-100">
                          {Math.round(child.stats.averageScore * 10) / 10}%
                        </p>
                      </div>
                    )}

                    {/* Accuracy Rate */}
                    {child.stats.accuracyRate !== null && (
                      <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-2.5">
                        <div className="flex items-center gap-1.5 mb-1">
                          <TrendingUp className="w-3.5 h-3.5 text-gray-500 dark:text-gray-400" />
                          <span className="text-xs text-gray-600 dark:text-gray-400">Accuracy</span>
                        </div>
                        <p className="text-base font-semibold text-gray-900 dark:text-gray-100">
                          {Math.round(child.stats.accuracyRate * 10) / 10}%
                        </p>
                      </div>
                    )}

                    {/* Questions Answered */}
                    <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-2.5">
                      <div className="flex items-center gap-1.5 mb-1">
                        <FileText className="w-3.5 h-3.5 text-gray-500 dark:text-gray-400" />
                        <span className="text-xs text-gray-600 dark:text-gray-400">Questions</span>
                      </div>
                      <p className="text-base font-semibold text-gray-900 dark:text-gray-100">
                        {child.stats.totalQuestionsAnswered || 0}
                      </p>
                    </div>

                    {/* Last Activity */}
                    {child.stats.lastActivity && (
                      <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-2.5">
                        <div className="flex items-center gap-1.5 mb-1">
                          <Calendar className="w-3.5 h-3.5 text-gray-500 dark:text-gray-400" />
                          <span className="text-xs text-gray-600 dark:text-gray-400">
                            Last Active
                          </span>
                        </div>
                        <p className="text-xs font-semibold text-gray-900 dark:text-gray-100">
                          {new Date(child.stats.lastActivity).toLocaleDateString(undefined, {
                            month: 'short',
                            day: 'numeric'
                          })}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <div className="mt-2 p-4 bg-gray-50 dark:bg-gray-800/50 rounded-lg text-center border border-gray-200 dark:border-gray-700">
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    No challenges completed yet
                  </p>
                </div>
              )}

              {/* Coaches Section */}
              {child.coaches && child.coaches.length > 0 && (
                <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                  <div className="flex items-center gap-2 mb-2">
                    <UserCheck className="w-4 h-4 text-indigo-500" />
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                      Coaches ({child.coaches.length})
                    </span>
                  </div>
                  <div className="flex flex-col gap-2">
                    {child.coaches.map((coach) => {
                      const status = coach.relationshipStatus || 'active'
                      const badge = STATUS_BADGE[status]
                      const guardianSigned = coach.signedRoles?.includes('guardian')
                      const needsConsent =
                        status === 'pending_agreements' && isMinor && !guardianSigned
                      return (
                        <div
                          key={coach.id}
                          className="flex items-center gap-2 flex-wrap px-3 py-1.5 bg-indigo-50 dark:bg-indigo-900/30 border border-indigo-200 dark:border-indigo-800 rounded-lg"
                        >
                          <div className="w-6 h-6 rounded-full bg-indigo-500 flex items-center justify-center text-white text-xs font-medium">
                            {coach.firstName?.charAt(0) || coach.name?.charAt(0) || '?'}
                          </div>
                          <span className="text-sm text-indigo-700 dark:text-indigo-300">
                            {coach.firstName && coach.lastName
                              ? `${coach.firstName} ${coach.lastName}`
                              : coach.name || coach.email}
                          </span>
                          {badge && (
                            <span
                              className={`px-2 py-0.5 text-xs font-medium rounded-full ${badge.cls}`}
                            >
                              {badge.label}
                            </span>
                          )}
                          {needsConsent && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation()
                                onSignConsent?.(coach.relationshipId)
                              }}
                              className="btn btn-primary btn-sm inline-flex items-center gap-1 ml-auto"
                            >
                              <FileSignature className="w-3.5 h-3.5" />
                              Sign consent
                            </button>
                          )}
                          {status === 'pending_agreements' && isMinor && guardianSigned && (
                            <span className="ml-auto text-xs text-green-700 dark:text-green-400 font-medium">
                              Consent signed
                            </span>
                          )}
                          {status === 'active' && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation()
                                navigate(
                                  `/messages?conversationType=coaching&childId=${child.id}&coachId=${coach.id}`
                                )
                              }}
                              className="ml-auto p-1 hover:bg-indigo-100 dark:hover:bg-indigo-800 rounded-full transition-colors"
                              title="View messages with this coach"
                            >
                              <MessageSquare className="w-3.5 h-3.5 text-indigo-600 dark:text-indigo-400" />
                            </button>
                          )}
                        </div>
                      )
                    })}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* View Details Button */}
          <div className="flex sm:flex-col gap-2 sm:ml-auto">
            <button
              onClick={handleViewDetails}
              className="btn btn-primary inline-flex items-center gap-2 whitespace-nowrap"
            >
              <FileText className="w-4 h-4" />
              View Details
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default ChildProgressCard
